/**
 * Domain Adapter — Buildings / BIM (IFC 4.3)
 *
 * Real implementation per buildings-ifc.md §5 (sidecar contract) and §2–§3
 * (field-mapping tables). Converts a SceneItem with `layerKind: "bim"` or
 * `"ifc-glb"` into a Tier-2 SceneHierarchyManifest.
 *
 * Hierarchy: IfcBuilding (LoR3) → IfcBuildingStorey (LoR4)
 *              → discipline group (LoR5) → IfcElement (LoR6)
 *
 * When `context.fetchJson` is absent or the sidecar fetch fails, a
 * fully-formed synthetic manifest is returned instead of an error.
 * The synthetic path is tested and must never produce an empty manifest.
 *
 * Per OBJECT_IDENTITY_AND_SELECTION_POLICY §7 / DESIGN_POLICY §5:
 * no domain-specific logic leaks into shared viewer code.
 */

import type { SceneItem } from "@/sdk-stub/gtwin-sdk/types";
import type {
  TreeNode,
  SceneHierarchyManifest,
  TreeNodeDomain,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type {
  DomainAdapter,
  DomainAdapterContext,
  AdapterResult,
} from "../DomainAdapter";

// ─────────────────────────────────────────────────────────────────────────────
// § 1  ULID generator (inline — no external dependency)
// ─────────────────────────────────────────────────────────────────────────────

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/**
 * Generates a 26-character ULID (Universally Unique Lexicographically Sortable
 * Identifier) using the current timestamp and cryptographically random bytes.
 * Alphabet: Crockford base32.
 */
function generateUlid(): string {
  let t = Date.now();
  const tsChars: string[] = [];
  for (let i = 0; i < 10; i++) {
    tsChars.unshift(CROCKFORD[t % 32]);
    t = Math.floor(t / 32);
  }
  const randBytes = new Uint8Array(10);
  crypto.getRandomValues(randBytes);
  let rand = "";
  for (let i = 0; i < 10; i++) {
    rand += CROCKFORD[randBytes[i] & 0x1f];
    rand += CROCKFORD[(randBytes[i] >> 5) & 0x07 | ((i < 9 ? randBytes[i + 1] & 0x03 : 0) << 3)];
  }
  return tsChars.join("") + rand.slice(0, 16);
}

// ─────────────────────────────────────────────────────────────────────────────
// § 2  Sidecar types (buildings-ifc.md §5.3)
// ─────────────────────────────────────────────────────────────────────────────

interface SidecarFeature {
  featureId: number;
  ifcGlobalId: string;
  ifcType: string;
  name: string;
  discipline: string;
  storey: string;
  usage?: string;
  propertySets?: Array<{ psetName: string; properties: Record<string, unknown> }>;
  bimForumLOD?: number;
  cobieComponentName?: string;
}

interface Sidecar {
  schema: string;
  layerId: string;
  generatedAt?: string;
  features: SidecarFeature[];
}

/** Type guard for the sidecar format from buildings-ifc.md §5.3. */
function isValidSidecar(raw: unknown): raw is Sidecar {
  if (typeof raw !== "object" || raw === null) return false;
  const r = raw as Record<string, unknown>;
  return (
    typeof r["schema"] === "string" &&
    r["schema"].startsWith("geosoft.properties-sidecar") &&
    Array.isArray(r["features"]) &&
    (r["features"] as unknown[]).every(
      (f) =>
        typeof f === "object" &&
        f !== null &&
        typeof (f as Record<string, unknown>)["ifcType"] === "string",
    )
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// § 3  Extended SceneItem type (for extensions.geosoft.*)
// ─────────────────────────────────────────────────────────────────────────────

type ExtendedSceneItem = SceneItem & {
  extensions?: {
    geosoft?: {
      domain?: TreeNodeDomain;
      standard?: string;
    };
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// § 4  Helpers
// ─────────────────────────────────────────────────────────────────────────────

function countByLoR(nodes: TreeNode[]): Record<number, number> {
  const counts: Record<number, number> = {};
  for (const n of nodes) {
    counts[n.lor] = (counts[n.lor] ?? 0) + 1;
  }
  return counts;
}

/** Returns the USN fields that must be set on every emitted node (Block 1). */
function usnMeta(id: string, parentId: string | null): {
  objectId: string;
  aspects: { spatial: { parentId: string | null }; functional: { parentId: string | null } };
  relationships: [];
} {
  return {
    objectId: id,
    aspects: {
      spatial: { parentId },
      functional: { parentId },
    },
    relationships: [],
  };
}

const IFC_CLASS_BASE =
  "https://identifier.buildingsmart.org/uri/buildingsmart/ifc/4.3/class/";

/** Maps IFC entity names to bSDD classification URIs (buildings-ifc.md §3.2). */
function ifcClassUri(ifcType: string): string {
  return `${IFC_CLASS_BASE}${ifcType}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// § 5  Synthetic manifest builder
//     Returns a real, well-formed manifest when no sidecar is available.
// ─────────────────────────────────────────────────────────────────────────────

interface SyntheticFlags {
  sidecarParseFailed?: boolean;
}

function buildSyntheticManifest(
  item: SceneItem,
  asset: { layerId: string; assetNodeId: string; gtwinId: string },
  flags: SyntheticFlags,
): AdapterResult {
  const rootId = generateUlid();
  const nodes: TreeNode[] = [];

  const rootMeta: Record<string, unknown> = { synthetic: true };
  if (flags.sidecarParseFailed !== undefined) {
    rootMeta["sidecarParseFailed"] = flags.sidecarParseFailed;
  }

  const root: TreeNode = {
    id: rootId,
    parentId: null,
    lor: 3,
    label: asset.assetNodeId || asset.layerId,
    kind: "asset",
    layerId: asset.layerId,
    featureId: null,
    domain: "building",
    standardRef: {
      standard: "IFC:4.3",
      classUri: ifcClassUri("IfcBuilding"),
      nativeId: asset.assetNodeId,
      propertySets: ["Pset_BuildingCommon"],
      linkedDocuments: [],
    },
    metadata: rootMeta,
    hasChildren: true,
    sortKey: 0,
    ...usnMeta(rootId, null),
  };
  nodes.push(root);

  const disciplines: Array<{ label: string; sortKey: number }> = [
    { label: "Architectural", sortKey: 0 },
    { label: "Structural", sortKey: 1 },
    { label: "Mechanical", sortKey: 2 },
    { label: "Electrical", sortKey: 3 },
  ];

  for (const d of disciplines) {
    const discId = rootId + "-" + d.sortKey;
    nodes.push({
      id: discId,
      parentId: rootId,
      lor: 5,
      label: d.label,
      kind: "discipline",
      layerId: null,
      featureId: null,
      domain: "building",
      standardRef: null,
      metadata: { synthesised: true },
      hasChildren: false,
      sortKey: d.sortKey,
      ...usnMeta(discId, rootId),
    });
  }

  const manifest: SceneHierarchyManifest = {
    schema: "geosoft.scene-hierarchy/1",
    gtwinId: asset.gtwinId,
    rootLayerId: asset.layerId,
    generatedAt: new Date().toISOString(),
    domain: "building",
    standardName: "IFC",
    standardVersion: "4.3",
    totalNodes: nodes.length,
    nodeCountByLoR: countByLoR(nodes),
    nodes,
    paginated: false,
    dataSources: {
      [asset.layerId]: {
        layerId: asset.layerId,
        label: "IFC Model",
        mediaKind: item.layerKind,
        primary: true,
      },
    },
  };

  return { ok: true, manifest };
}

// ─────────────────────────────────────────────────────────────────────────────
// § 6  Sidecar-based manifest builder
//     Produces a real hierarchy from the properties sidecar (buildings-ifc.md §5.3).
//     Maps: IfcBuilding (LoR3) → storey (LoR4) → discipline (LoR5) → element (LoR6)
// ─────────────────────────────────────────────────────────────────────────────

function buildFromSidecar(
  item: SceneItem,
  asset: { layerId: string; assetNodeId: string; gtwinId: string },
  sidecar: Sidecar,
): AdapterResult {
  const nodes: TreeNode[] = [];
  const rootId = generateUlid();

  const root: TreeNode = {
    id: rootId,
    parentId: null,
    lor: 3,
    label: asset.assetNodeId || asset.layerId,
    kind: "asset",
    layerId: asset.layerId,
    featureId: null,
    domain: "building",
    standardRef: {
      standard: "IFC:4.3",
      classUri: ifcClassUri("IfcBuilding"),
      nativeId: asset.assetNodeId,
      propertySets: ["Pset_BuildingCommon"],
      linkedDocuments: [],
    },
    metadata: {},
    hasChildren: sidecar.features.length > 0,
    sortKey: 0,
    ...usnMeta(rootId, null),
  };
  nodes.push(root);

  // Group features by storey (buildings-ifc.md §2.1: IfcBuildingStorey → LoR4)
  const storeyMap = new Map<string, SidecarFeature[]>();
  for (const f of sidecar.features) {
    const key = f.storey ?? "Unknown Storey";
    if (!storeyMap.has(key)) storeyMap.set(key, []);
    storeyMap.get(key)!.push(f);
  }

  let storeyIdx = 0;
  for (const [storeyLabel, features] of storeyMap) {
    const storeyId = generateUlid();
    const storeyNode: TreeNode = {
      id: storeyId,
      parentId: rootId,
      lor: 4,
      label: storeyLabel,
      kind: "group",
      layerId: asset.layerId,
      featureId: null,
      domain: "building",
      standardRef: {
        standard: "IFC:4.3",
        classUri: ifcClassUri("IfcBuildingStorey"),
        nativeId: `storey-${storeyIdx}`,
        propertySets: ["Pset_BuildingStoreyCommon"],
        linkedDocuments: [],
      },
      metadata: { storeyIndex: storeyIdx },
      hasChildren: features.length > 0,
      sortKey: storeyIdx,
      ...usnMeta(storeyId, rootId),
    };
    nodes.push(storeyNode);

    // Group by discipline under each storey (buildings-ifc.md §2.1: synthesised LoR5 discipline)
    const discMap = new Map<string, SidecarFeature[]>();
    for (const f of features) {
      const d = f.discipline ?? "Uncategorised";
      if (!discMap.has(d)) discMap.set(d, []);
      discMap.get(d)!.push(f);
    }

    let discIdx = 0;
    for (const [discLabel, discFeatures] of discMap) {
      const discId = generateUlid();
      const discNode: TreeNode = {
        id: discId,
        parentId: storeyId,
        lor: 5,
        label: discLabel,
        kind: "discipline",
        layerId: null,
        featureId: null,
        domain: "building",
        standardRef: null,
        metadata: { synthesised: true, elementCount: discFeatures.length },
        hasChildren: discFeatures.length > 0,
        sortKey: discIdx,
        ...usnMeta(discId, storeyId),
      };
      nodes.push(discNode);

      let elemIdx = 0;
      for (const f of discFeatures) {
        const elemId = generateUlid();

        // Field mapping per buildings-ifc.md §3.1 and §3.2
        const psetNames =
          f.propertySets?.map((ps) => ps.psetName) ?? [];
        const psetMetadata: Record<string, unknown> = {};
        for (const ps of f.propertySets ?? []) {
          for (const [k, v] of Object.entries(ps.properties)) {
            psetMetadata[`${ps.psetName}.${k}`] = v;
          }
        }

        const elemLabel =
          f.name ||
          `${f.ifcType} [${(f.ifcGlobalId ?? "").slice(-4)}]`;

        const elemNode: TreeNode = {
          id: elemId,
          parentId: discId,
          lor: 6,
          label: elemLabel,
          kind: "element",
          layerId: asset.layerId,
          featureId: `feature:${f.featureId}`,
          domain: "building",
          standardRef: {
            standard: "IFC:4.3",
            classUri: ifcClassUri(f.ifcType),
            nativeId: f.ifcGlobalId,
            propertySets: psetNames,
            linkedDocuments: [],
          },
          metadata: {
            ifcType: f.ifcType,
            storey: f.storey,
            discipline: f.discipline,
            "COBie.Component.ExternalIdentifier": f.ifcGlobalId,
            ...(f.cobieComponentName
              ? { "COBie.Component.Name": f.cobieComponentName }
              : {}),
            ...(f.bimForumLOD !== undefined
              ? { "BIMForum.LOD": f.bimForumLOD }
              : {}),
            ...psetMetadata,
          },
          hasChildren: false,
          sortKey: elemIdx,
          ...usnMeta(elemId, discId),
        };
        nodes.push(elemNode);
        elemIdx++;
      }
      discIdx++;
    }
    storeyIdx++;
  }

  const manifest: SceneHierarchyManifest = {
    schema: "geosoft.scene-hierarchy/1",
    gtwinId: asset.gtwinId,
    rootLayerId: asset.layerId,
    generatedAt: new Date().toISOString(),
    domain: "building",
    standardName: "IFC",
    standardVersion: "4.3",
    totalNodes: nodes.length,
    nodeCountByLoR: countByLoR(nodes),
    nodes,
    paginated: false,
    dataSources: {
      [asset.layerId]: {
        layerId: asset.layerId,
        label: "IFC Model",
        mediaKind: item.layerKind,
        primary: true,
      },
    },
  };

  return { ok: true, manifest };
}

// ─────────────────────────────────────────────────────────────────────────────
// § 7  Adapter implementation
// ─────────────────────────────────────────────────────────────────────────────

export const IfcBuildingAdapter: DomainAdapter = {
  domain: "building" as const,
  version: "1.0.0",
  standard: "IFC:4.3",

  /**
   * Detects IFC/BIM assets.
   * Returns true when `layerKind` is `"bim"` or `"ifc-glb"`, or when
   * `extensions.geosoft.standard` starts with `"IFC"`.
   *
   * buildings-ifc.md §6.1 (detect contract).
   */
  detect(item: SceneItem): boolean {
    const ext = (item as ExtendedSceneItem).extensions?.geosoft;
    if (ext?.standard?.startsWith("IFC")) return true;
    return item.layerKind === "bim" || item.layerKind === "ifc-glb";
  },

  /**
   * Builds the Tier-2 IFC manifest.
   *
   * 1. If `context.fetchJson` is provided, attempts to fetch the sidecar at
   *    `{endpoint.href}.properties.json` (buildings-ifc.md §5.3).
   * 2. If fetch fails → synthetic manifest (`sidecarParseFailed: false`).
   * 3. If sidecar is malformed → synthetic with `sidecarParseFailed: true`.
   * 4. If sidecar is valid → full IfcBuilding→Storey→Discipline→Element tree.
   * 5. If no `fetchJson` → synthetic without `sidecarParseFailed` flag.
   *
   * Never throws. Never returns an empty manifest.
   *
   * buildings-ifc.md §2–§3 field-mapping tables; §5.1–§5.3 sidecar format.
   */
  async buildManifest(
    item: SceneItem,
    asset: { layerId: string; assetNodeId: string; gtwinId: string },
    context: DomainAdapterContext,
  ): Promise<AdapterResult> {
    const href =
      item.endpoints["href"] ?? Object.values(item.endpoints)[0] ?? "";

    if (!context.fetchJson) {
      return buildSyntheticManifest(item, asset, {});
    }

    const sidecarUrl = `${href}.properties.json`;

    let raw: unknown;
    try {
      raw = await context.fetchJson(sidecarUrl);
    } catch {
      // Sidecar absent (404) or network failure → synthetic, not an error
      return buildSyntheticManifest(item, asset, { sidecarParseFailed: false });
    }

    if (!isValidSidecar(raw)) {
      // Sidecar present but malformed
      return buildSyntheticManifest(item, asset, { sidecarParseFailed: true });
    }

    try {
      return buildFromSidecar(item, asset, raw);
    } catch (cause) {
      return buildSyntheticManifest(item, asset, { sidecarParseFailed: true });
    }
  },
};
